import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UniqueIdentifier } from '@dnd-kit/core';
import {
  Kanban,
  KanbanBoard,
  KanbanColumn,
  KanbanItem as KanbanItemPrimitive,
  KanbanItemHandle,
  KanbanOverlay,
} from '@/components/ui/kanban';
import PageHeader from '@/components/shared/PageHeader';
import { usePageTitle } from '@/hooks/usePageTitle';
import { authService } from '@/services/authService';
import { warehouseService, type KanbanPackage, type Package as WarehousePackage } from '@/services/warehouseService';
import { useOrderSheet } from '@/components/orders/OrderSheet';
import { ShippingBookingModal } from '@/components/warehouse/ShippingBookingModal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Package,
  CheckCircle,
  Truck,
  User,
  Clock,
  AlertTriangle,
  RefreshCw,
  Eye,
  GripVertical,
  Send,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ── Types ────────────────────────────────────────────────────────────────

type WarehouseStatus = 'not_shipped' | 'sent_to_packing' | 'packed' | 'delivery_booked' | 'shipped' | 'delivered';
type PipelineStatus = 'not_shipped' | 'sent_to_packing' | 'packed' | 'delivery_booked';

interface KanbanItem extends KanbanPackage {
  _kanbanId: string;
}

const COLUMNS: { id: PipelineStatus; label: string; shortLabel: string; icon: React.ReactNode; color: string; bg: string }[] = [
  { id: 'not_shipped', label: 'Ready to Pack', shortLabel: 'Ready', icon: <Package size={14} />, color: 'text-slate-400', bg: 'bg-slate-500/5' },
  { id: 'sent_to_packing', label: 'Sent to Packing', shortLabel: 'Packing', icon: <Send size={14} />, color: 'text-amber-500', bg: 'bg-amber-500/5' },
  { id: 'packed', label: 'Packed', shortLabel: 'Packed', icon: <Package size={14} />, color: 'text-blue-500', bg: 'bg-blue-500/5' },
  { id: 'delivery_booked', label: 'Delivery Booked', shortLabel: 'Booked', icon: <Truck size={14} />, color: 'text-purple-500', bg: 'bg-purple-500/5' },
];

const STATUS_ORDER: PipelineStatus[] = ['not_shipped', 'sent_to_packing', 'packed', 'delivery_booked'];

function getNextStatus(current: PipelineStatus): PipelineStatus | null {
  const idx = STATUS_ORDER.indexOf(current);
  return idx < STATUS_ORDER.length - 1 ? STATUS_ORDER[idx + 1] : null;
}

function getActionText(status: PipelineStatus): string | null {
  switch (status) {
    case 'not_shipped': return 'Send to Packing';
    case 'sent_to_packing': return 'Mark Packed';
    case 'packed': return 'Book Delivery';
    default: return null;
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(amount);
}

// ── Compact Package Card ─────────────────────────────────────────────────

function PackageCard({
  pkg,
  onStatusUpdate,
  onBookDelivery,
  onCreatePackage,
  isUpdating,
  isDragOverlay = false,
}: {
  pkg: KanbanItem;
  onStatusUpdate: (packageId: number, newStatus: WarehouseStatus) => void;
  onBookDelivery: (pkg: KanbanItem) => void;
  onCreatePackage: (pkg: KanbanItem) => void;
  isUpdating: boolean;
  isDragOverlay?: boolean;
}) {
  const { openOrder } = useOrderSheet();
  const status = pkg.warehouse_status as PipelineStatus;
  const nextStatus = getNextStatus(status);
  const actionText = getActionText(status);

  // Peek at the order without leaving the board. This is how delivery
  // constraints buried in the order notes ("no pallets, shop door only")
  // finally reach the person about to pack the box.
  const peek = () =>
    openOrder(pkg.order_id, {
      salesorder_number: pkg.salesorder_number,
      customer_name: pkg.customer_name,
      total: pkg.order_total,
    });

  return (
    <div
      className={cn(
        'rounded-md border bg-card px-2.5 py-2 shadow-sm',
        isDragOverlay && 'shadow-lg ring-2 ring-primary/20 rotate-[1deg]',
      )}
    >
      {/* Rows 1–2 open the order sheet; the action button below is separate */}
      <button
        type="button"
        onClick={peek}
        disabled={isDragOverlay}
        className="w-full cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 rounded-sm"
        title={`Preview ${pkg.salesorder_number}`}
      >
        {/* Row 1: customer name (headline) */}
        <div className="flex items-center gap-1.5 mb-0.5">
          <User size={11} className="shrink-0 text-muted-foreground" />
          <span className="text-xs font-semibold text-foreground truncate">
            {pkg.customer_name || 'Unknown'}
          </span>
        </div>

        {/* Row 2: packing number + order number + items */}
        <div className="flex items-center justify-between gap-1 text-[11px] text-muted-foreground">
          {pkg.packing_number && <span className="truncate">{pkg.packing_number}</span>}
          <span className="text-[10px] text-primary shrink-0">{pkg.salesorder_number}</span>
          <span className="shrink-0 tabular-nums">{pkg.item_count} item{pkg.item_count !== 1 ? 's' : ''}</span>
        </div>
      </button>

      {/* Row 3: action button (compact) */}
      {nextStatus && actionText && !isDragOverlay && (
        <Button
          size="xs"
          intent="outline"
          className="w-full mt-1.5 h-6 text-[11px]"
          isDisabled={isUpdating}
          onPress={() => {
            // An order-sourced card has no package yet — pack it into a new one.
            if (pkg.source === 'order') return onCreatePackage(pkg);
            if (status === 'packed') return onBookDelivery(pkg);
            if (pkg.id !== null) return onStatusUpdate(pkg.id, nextStatus);
          }}
        >
          {isUpdating ? (
            <RefreshCw size={10} className="animate-spin" />
          ) : (
            <CheckCircle size={10} />
          )}
          {isUpdating ? 'Updating...' : actionText}
        </Button>
      )}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────

export default function Warehouse() {
  usePageTitle('Warehouse');
  const navigate = useNavigate();

  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingPackages, setUpdatingPackages] = useState<Set<number>>(new Set());
  const [updatingOrders, setUpdatingOrders] = useState<Set<number>>(new Set());
  const [bookingPackage, setBookingPackage] = useState<WarehousePackage | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const [columns, setColumns] = useState<Record<UniqueIdentifier, KanbanItem[]>>({
    not_shipped: [],
    sent_to_packing: [],
    packed: [],
    delivery_booked: [],
  });

  useEffect(() => {
    const agent = authService.getCachedAgent();
    if (!agent) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }
    setAuthenticated(true);
  }, []);

  const fetchPackages = useCallback(async () => {
    if (!authenticated) return;
    try {
      setError(null);
      const data = await warehouseService.getKanbanData();

      // Ready cards are order-sourced and have no package id, so key them by
      // order_id — otherwise every one of them collides on `${status}-null`.
      const enrich = (packages: KanbanPackage[], status: WarehouseStatus): KanbanItem[] =>
        packages.map((p) => ({
          ...p,
          warehouse_status: status,
          _kanbanId:
            p.source === 'order' ? `order-${p.order_id}` : `${status}-${p.id}`,
        }));

      setColumns({
        not_shipped: enrich(data.not_shipped, 'not_shipped'),
        sent_to_packing: enrich(data.sent_to_packing, 'sent_to_packing'),
        packed: enrich(data.packed, 'packed'),
        delivery_booked: enrich(data.delivery_booked, 'delivery_booked'),
      });
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching warehouse packages:', err);
      setError('Failed to fetch warehouse packages');
    } finally {
      setLoading(false);
    }
  }, [authenticated]);

  useEffect(() => {
    if (authenticated) fetchPackages();
  }, [fetchPackages, authenticated]);

  useEffect(() => {
    if (!authenticated) return;
    const interval = setInterval(fetchPackages, 30000);
    return () => clearInterval(interval);
  }, [fetchPackages, authenticated]);

  const handleStatusUpdate = useCallback(
    async (packageId: number, newStatus: WarehouseStatus) => {
      setUpdatingPackages((prev) => new Set([...prev, packageId]));
      try {
        await warehouseService.updatePackageStatus(packageId, newStatus);
        await fetchPackages();
      } catch (err) {
        console.error('Error updating package status:', err);
        setError(err instanceof Error ? err.message : 'Failed to update package status');
        setTimeout(() => setError(null), 5000);
      } finally {
        setUpdatingPackages((prev) => {
          const next = new Set(prev);
          next.delete(packageId);
          return next;
        });
      }
    },
    [fetchPackages],
  );

  const handleBookDelivery = useCallback(async (pkg: KanbanItem) => {
    if (pkg.id === null) return;
    try {
      setBookingPackage(await warehouseService.getPackage(pkg.id));
    } catch (err) {
      console.error('Error loading package for delivery booking:', err);
      setError('Failed to load package for delivery booking');
    }
  }, []);

  // Ready cards are orders with no package yet, so "Send to Packing" creates
  // one containing everything still outstanding.
  const handleCreatePackage = useCallback(
    async (pkg: KanbanItem) => {
      setUpdatingOrders((prev) => new Set([...prev, pkg.order_id]));
      try {
        await warehouseService.createPackageForOrder(pkg.order_id);
        await fetchPackages();
      } catch (err) {
        console.error('Error creating package for order:', err);
        setError(err instanceof Error ? err.message : 'Failed to send order to packing');
        setTimeout(() => setError(null), 5000);
      } finally {
        setUpdatingOrders((prev) => {
          const next = new Set(prev);
          next.delete(pkg.order_id);
          return next;
        });
      }
    },
    [fetchPackages],
  );

  const handleValueChange = useCallback(
    async (newColumns: Record<UniqueIdentifier, KanbanItem[]>) => {
      const prevColumns = columns;
      let movedPackage: KanbanItem | null = null;
      let targetColumn: WarehouseStatus | null = null;

      for (const colId of STATUS_ORDER) {
        const newItems = newColumns[colId] ?? [];
        const prevItems = prevColumns[colId] ?? [];
        const prevIds = new Set(prevItems.map((p) => p._kanbanId));
        for (const item of newItems) {
          if (!prevIds.has(item._kanbanId)) {
            movedPackage = item;
            targetColumn = colId;
            break;
          }
        }
        if (movedPackage) break;
      }

      setColumns(newColumns);

      if (movedPackage && targetColumn) {
        try {
          if (movedPackage.source === 'order') {
            // No package exists yet. Creating one puts it in sent_to_packing,
            // so any other target would be a state the order can't be in.
            if (targetColumn !== 'sent_to_packing') {
              throw new Error('An order must be sent to packing before it can move further');
            }
            await warehouseService.createPackageForOrder(movedPackage.order_id);
          } else if (movedPackage.id !== null) {
            await warehouseService.updatePackageStatus(movedPackage.id, targetColumn);
          }
          await fetchPackages();
        } catch (err) {
          console.error('Drag-drop update failed:', err);
          setError(err instanceof Error ? err.message : 'Failed to update package via drag-and-drop');
          setTimeout(() => setError(null), 4000);
          setColumns(prevColumns);
        }
      }
    },
    [columns, fetchPackages],
  );

  const stats = useMemo(() => {
    const allPackages = Object.values(columns).flat();
    return {
      total: allPackages.length,
      value: allPackages.reduce((sum, p) => sum + (p.order_total || 0), 0),
    };
  }, [columns]);

  const findPackage = useCallback(
    (id: UniqueIdentifier): KanbanItem | undefined => {
      for (const items of Object.values(columns)) {
        const found = items.find((p) => p._kanbanId === id);
        if (found) return found;
      }
      return undefined;
    },
    [columns],
  );

  const columnCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const col of COLUMNS) {
      counts[col.id] = (columns[col.id] ?? []).length;
    }
    return counts;
  }, [columns]);

  if (loading) {
    return (
      <div className="p-4 space-y-4">
        <PageHeader
          title="Warehouse"
          subtitle="Loading..."
          breadcrumbs={[
            { label: 'Shipping', href: '/shipping/warehouse' },
            { label: 'Warehouse' },
          ]}
        />
        <div className="flex items-center justify-center py-20">
          <RefreshCw size={24} className="animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 h-[calc(100vh-64px)] flex flex-col">
      {/* Page Header — compact */}
      <PageHeader
        title="Warehouse"
        count={stats.total}
        subtitle="Active packing workflow. Track in-transit and delivered packages from Deliveries."
        breadcrumbs={[
          { label: 'Shipping', href: '/shipping/warehouse' },
          { label: 'Warehouse' },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button
              intent="outline"
              size="xs"
              onPress={() => navigate('/shipping/packing-lists')}
            >
              Packing Lists
            </Button>
            <Button
              intent="outline"
              size="xs"
              onPress={() => navigate('/shipping/deliveries')}
            >
              Deliveries
            </Button>
            <span className="hidden lg:flex items-center gap-1 text-xs text-muted-foreground">
              <Clock size={11} />
              {lastRefresh.toLocaleTimeString()}
            </span>
            <Button
              intent="outline"
              size="xs"
              onPress={fetchPackages}
              isDisabled={loading}
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
              Refresh
            </Button>
          </div>
        }
      />

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs text-destructive shrink-0">
          <AlertTriangle size={14} />
          <span>{error}</span>
        </div>
      )}

      {/* Kanban Board — fills remaining height */}
      <div className="flex-1 min-h-0 overflow-x-auto">
        <Kanban<KanbanItem>
          value={columns}
          onValueChange={handleValueChange}
          getItemValue={(item) => item._kanbanId}
          flatCursor
        >
          <KanbanBoard className="gap-3 h-full min-w-max">
            {COLUMNS.map((col) => (
              <KanbanColumn
                key={col.id}
                value={col.id}
                className={cn(
                  'w-[280px] flex-1 rounded-lg border border-border/40 p-0 gap-0 flex flex-col h-full',
                  col.bg,
                )}
              >
                {/* Column header */}
                <div className="flex items-center justify-between px-2.5 py-2 border-b border-border/40 shrink-0">
                  <div className="flex items-center gap-1.5">
                    <span className={col.color}>{col.icon}</span>
                    <span className="text-xs font-medium text-foreground">{col.shortLabel}</span>
                  </div>
                  <Badge
                    variant="secondary"
                    className="text-[10px] tabular-nums h-4 min-w-[18px] justify-center px-1"
                  >
                    {columnCounts[col.id] ?? 0}
                  </Badge>
                </div>

                {/* Column body — scrollable */}
                <div className="flex flex-col gap-1.5 p-1.5 flex-1 min-h-0 overflow-y-auto">
                  {(columns[col.id] ?? []).map((pkg) => (
                    <KanbanItemPrimitive
                      key={pkg._kanbanId}
                      value={pkg._kanbanId}
                      className="rounded-md"
                    >
                      <div className="flex gap-0.5">
                        <KanbanItemHandle className="flex items-center px-0.5 text-muted-foreground/30 hover:text-muted-foreground/60 transition-colors">
                          <GripVertical size={12} />
                        </KanbanItemHandle>
                        <div className="flex-1 min-w-0">
                          <PackageCard
                            pkg={pkg}
                            onStatusUpdate={handleStatusUpdate}
                            onBookDelivery={handleBookDelivery}
                            onCreatePackage={handleCreatePackage}
                            isUpdating={
                              pkg.source === 'order'
                                ? updatingOrders.has(pkg.order_id)
                                : pkg.id !== null && updatingPackages.has(pkg.id)
                            }
                          />
                        </div>
                      </div>
                    </KanbanItemPrimitive>
                  ))}

                  {/* Empty state */}
                  {(columns[col.id] ?? []).length === 0 && (
                    <div className="flex flex-col items-center justify-center py-6 text-muted-foreground/40">
                      <span className={cn('mb-1', col.color)}>{col.icon}</span>
                      <span className="text-[10px]">No packages</span>
                    </div>
                  )}
                </div>
              </KanbanColumn>
            ))}
          </KanbanBoard>

          {/* Drag overlay */}
          <KanbanOverlay>
            {({ value, variant }) => {
              if (variant === 'column') return null;
              const pkg = findPackage(value);
              if (!pkg) return null;
              return (
                <div className="w-[270px]">
                  <PackageCard
                    pkg={pkg}
                    onStatusUpdate={handleStatusUpdate}
                    onBookDelivery={handleBookDelivery}
                    onCreatePackage={handleCreatePackage}
                    isUpdating={false}
                    isDragOverlay
                  />
                </div>
              );
            }}
          </KanbanOverlay>
        </Kanban>
      </div>

      {bookingPackage && (
        <ShippingBookingModal
          pkg={bookingPackage}
          open={!!bookingPackage}
          onClose={() => setBookingPackage(null)}
          onSuccess={() => {
            setBookingPackage(null);
            fetchPackages();
          }}
        />
      )}
    </div>
  );
}
